import { Client } from '@lucidtech/las-sdk-browser';
import { queryOptions, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Buffer } from 'buffer';

import { useClient } from '@/hooks';
import { TokenCredentials } from '@/utils';

import { QueryOptions } from './useApi';

export type FileId = { fileUrl: string };
const QUERY_KEY = 'files';

export const getOptions = (client: Client, fileId?: FileId | null, queryOpts?: QueryOptions<File>) => {
  const { refetchInterval, enabled } = queryOpts || {};

  return queryOptions({
    queryKey: [(client.credentials as TokenCredentials).organizationId, QUERY_KEY, fileId],
    queryFn: async () => {
      if (!fileId) throw new Error('fileUrl is not defined');
      return await client.makeFileServerGetRequest(fileId.fileUrl);
    },
    staleTime: 15 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    enabled: !!fileId && enabled,
    retry: (failureCount, error) => {
      const statusCode = axios.isAxiosError(error) && error.response ? error.response.status : undefined;
      if (statusCode != null && statusCode >= 400 && statusCode < 500) {
        return false;
      }
      return failureCount < 3;
    },
    refetchInterval: refetchInterval,
  });
};

export const useGetFile = (fileId?: FileId | null, queryOpts?: QueryOptions<File>) => {
  const { client } = useClient();
  return useQuery(getOptions(client, fileId, queryOpts));
};

export const useBatchGetFile = <T>(fileIds?: (FileId | null | undefined)[] | null, opts?: Partial<T>) => {
  const { client } = useClient();

  return useQueries({
    queries: (fileIds ?? []).map((fileId) => ({
      ...getOptions(client, fileId),
      ...(opts || {}),
    })),
    combine: (results) => {
      return {
        data: results.map((result) => result.data).filter((d) => !!d),
        isPending: results.some((result) => result.isPending),
      };
    },
  });
};

export const usePutFile = () => {
  const { client } = useClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fileUrl, file }: { fileUrl: string; file: Blob | File }) => {
      const content = Buffer.from(await file.arrayBuffer());
      const headers = { 'Content-Type': file.type };
      const response = await client.makeFileServerPutRequest(fileUrl, content, { headers });
      return { fileUrl, response };
    },
    onSuccess: async ({ fileUrl, response }) => {
      await queryClient.invalidateQueries({
        queryKey: [(client.credentials as TokenCredentials).organizationId, QUERY_KEY],
        exact: true,
      });
      await queryClient.setQueryData(
        [(client.credentials as TokenCredentials).organizationId, QUERY_KEY, toFileId(fileUrl)],
        response
      );
    },
  });
};

export function toFileId<V extends FileId>(fileUrl: string): V;
export function toFileId<V extends FileId>(fileUrl?: string): V | null;
export function toFileId<V extends FileId>(fileUrl?: string | null): V | null;
export function toFileId<V extends FileId>(fileUrl?: string | null): V | null {
  return fileUrl ? ({ fileUrl } as V) : null;
}
